import { Skeleton } from '@westhetics/ui'
import type { ReactNode } from 'react'
import { Navigate, Outlet } from 'react-router'
import { useSession, type Role } from '@/lib/session'
import { routes } from '@/routes'
import { Page } from './shell'

/** Enquanto a sessão carrega: o esqueleto da página, nunca spinner central. */
export function PageSkeleton() {
  return (
    <Page>
      <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
        <Skeleton className="h-[26px] w-[240px]" />
        <Skeleton className="h-[13px] w-[360px] max-w-full" />
        <Skeleton className="mt-4 h-[180px] w-full" />
      </div>
    </Page>
  )
}

/** Só passa quem entrou. Sessão vencida ou ausente volta ao login (RN-AUT-19). */
export function RequireSession() {
  const session = useSession()
  if (session.status === 'loading') return <PageSkeleton />
  if (session.status === 'signed_out') return <Navigate to={routes.login} replace />
  // Senha temporária: nada além da troca (RT-04, RN-AUT-05).
  if (session.status === 'password_required') return <Navigate to={routes.newPassword} replace />
  return <Outlet />
}

/**
 * A porta de entrada de quem acabou de entrar: com mais de uma clínica utilizável (ou root), a
 * pessoa escolhe onde entrar antes de seguir (RN-AUT-11, P-AUT-02). Vale só na entrada: recarregar
 * a página ou trocar de clínica depois não pergunta de novo (P-AUT-18).
 */
export function RequireClinicChoice({ children }: { children: ReactNode }) {
  const session = useSession()
  if (session.mustChooseClinic) return <Navigate to={routes.chooseClinic} replace />
  return <>{children}</>
}

/**
 * Rota de um perfil só. O menu já escondeu o caminho; quem chega pelo endereço volta para onde
 * pode estar, sem tela de "acesso negado" (o que a pessoa não pode fazer some da vista).
 */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const session = useSession()
  const role = session.profile?.role
  if (!role || !roles.includes(role)) return <Navigate to={routes.dashboard} replace />
  return <>{children}</>
}

/** Rotas da clínica ativa. Root sem clínica vai para o painel de clínicas escolher uma (RT-03). */
export function RequireClinic({ children }: { children: ReactNode }) {
  const session = useSession()
  if (session.isGlobalRoot) return <Navigate to={routes.admin.clinics} replace />
  return <>{children}</>
}
